import { ElMessage } from "element-plus";
import { getAgentId } from "@/config/api";
import { getWidgetAgentData } from "@/utils/widgetAgent";
import { mapFormaPago, mapPaquete } from "@/utils/emissionMappers";
import {
  buildCotizacionesEmisionesInsertParams,
  buildUpdateQuoteParams,
  insertCotizacionesEmisiones,
  updateCotizacionAutosDetalle,
} from "@/services/emissionService";
import type { EmissionCoberturaContext } from "@/types/emission.types";
import type { EmissionSubmitDependencies } from "./contracts";
import { maintainExpandMode, toQuoteContext } from "./helpers";

export async function executeUpdateQuoteFlow(
  deps: EmissionSubmitDependencies,
): Promise<void> {
  const props = deps.getProps();
  const idCotizacion = props.quotesResponse?.Cobertura?.Id_Cotizacion;
  if (!idCotizacion) {
    ElMessage.error("No se encontró la cotización para actualizar");
    return;
  }

  const quote = deps.quoteStore.quoteData;
  const cotizacionData = quote.cotizacionData ?? {};
  const idCotizacionAutosDetalle = cotizacionData.idCotizacionAutosDetalle;
  if (!idCotizacionAutosDetalle) {
    ElMessage.error(
      "No se encontró el ID de cotización autos detalle para actualizar",
    );
    return;
  }

  const quoteContext = toQuoteContext(quote);
  const agentData = getWidgetAgentData();
  const cobertura = props.quotesResponse?.Cobertura as
    | EmissionCoberturaContext
    | undefined;

  const updateParams = buildUpdateQuoteParams(
    quoteContext,
    deps.formData,
    cobertura,
    agentData,
    Number(idCotizacion),
    idCotizacionAutosDetalle,
    props.selectedPaymentMethod,
    props.selectedPlanType,
  );

  deps.state.isEmitting.value = true;
  try {
    const response = await updateCotizacionAutosDetalle(updateParams);
    if (!response.success) {
      ElMessage.error(response.errorMessage || "Error al actualizar la cotización");
      return;
    }

    const idAgente =
      agentData.Id_Agente ?? (getAgentId() ? Number(getAgentId()) : null);
    const formaPago = mapFormaPago(props.selectedPaymentMethod);
    const paquete = mapPaquete(props.selectedPlanType);

    const insertParams = buildCotizacionesEmisionesInsertParams(
      quoteContext,
      deps.formData,
      cobertura,
      agentData,
      {
        idCotizacion: Number(idCotizacion),
        idAgente: idAgente ?? 0,
        idPoliza: 0,
        emitida: 0,
        formaPago,
        paquete,
        selectedPlanType: props.selectedPlanType,
        selectedPaymentMethod: props.selectedPaymentMethod,
        vehicleDescription: quote.model?.name ?? "",
      },
    );

    const insertResponse = await insertCotizacionesEmisiones(insertParams);
    if (!insertResponse.success) {
      ElMessage.warning(
        insertResponse.errorMessage ||
          "Cotización actualizada. No se pudo guardar para emisión posterior.",
      );
    }

    ElMessage.success("Cotización actualizada correctamente");
    deps.state.emissionSuccess.value = true;
    deps.emitSuccess({
      policyData: {
        planType: props.selectedPlanType,
        primaTotal:
          cobertura?.PrimaTotal != null ? Number(cobertura.PrimaTotal) : undefined,
      },
      documentos: [],
    });

    await deps.sendEmailInBackground({ idCotizacion: Number(idCotizacion) });
  } catch {
    ElMessage.error("Error al actualizar la cotización");
  } finally {
    deps.state.isEmitting.value = false;
    maintainExpandMode();
  }
}
